import { useEffect, useId, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import type { CorporateAction } from '@hjci-investorhub-v2/domain';
import { Button } from '../../components/ui/button';
import { useCanEdit } from '../auth/auth';

function tooltipPosition(button: HTMLButtonElement) {
  const rect = button.getBoundingClientRect();
  const boundary = button.closest('[role="dialog"]')?.getBoundingClientRect();
  const right = (boundary?.right ?? window.innerWidth) - 16;
  const width = Math.min(300, (boundary?.width ?? window.innerWidth) - 32);
  return { width, left: Math.min(0, right - rect.left - width) };
}

export function ActionEditButton({ status, onEdit }: { status: CorporateAction['status']; onEdit: () => void }) {
  const canEdit = useCanEdit();
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ left: number; width: number } | null>(null);
  const reason = status === 'posted' ? 'This event has already been applied to holdings. To correct it, reverse the event and create a replacement draft.'
    : status === 'reversed' ? 'This event has been reversed and is kept for history. Create a new draft to record a corrected event.'
    : status !== 'draft' ? 'Only draft events can be edited.'
    : !canEdit ? 'Your role allows viewing only. An editor or administrator can edit draft events.' : undefined;
  useEffect(() => {
    if (!position) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation(); setPosition(null);
    };
    const reposition = () => { if (button.current) setPosition(tooltipPosition(button.current)); };
    window.addEventListener('keydown', dismiss, true); window.addEventListener('resize', reposition);
    return () => { window.removeEventListener('keydown', dismiss, true); window.removeEventListener('resize', reposition); };
  }, [position]);
  const show = (button: HTMLButtonElement) => {
    if (!reason) return;
    setPosition(tooltipPosition(button));
  };
  return <span className="action-edit-tooltip" onMouseLeave={() => setPosition(null)}>
    <Button ref={button} type="button" size="icon" variant="ghost" className="action-edit-button" aria-label={reason ? 'Edit event' : 'Edit draft'} title={reason ? undefined : 'Edit draft'} aria-disabled={Boolean(reason)} aria-describedby={reason ? id : undefined}
      onMouseEnter={event => show(event.currentTarget)} onFocus={event => show(event.currentTarget)} onBlur={() => setPosition(null)}
      onClick={event => { if (reason) { event.preventDefault(); show(event.currentTarget); } else { setPosition(null); onEdit(); } }}><Pencil size={17} /></Button>
    {reason && <span id={id} role="tooltip" className="action-edit-help" hidden={!position} style={position ?? undefined}>{reason}</span>}
  </span>;
}
